import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, Percent, Store, Box } from 'lucide-react';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { vendorAdminApi } from '../services/api';

const empty = {
  code: '', scope: 'vendor', product_id: '',
  discount_type: 'percent', discount_value: 10,
  min_subtotal_cad: 0, max_discount_cad: null,
  description: '', active: true, expires_at: '', usage_limit: null,
};

const VendorPromotionsPage = () => {
  const [params] = useSearchParams();
  const vendorId = params.get('vendor_id') || undefined;
  const cfg = vendorId ? { params: { vendor_id: vendorId } } : {};
  const [items, setItems] = useState([]);
  const [menu, setMenu] = useState({ sections: [] });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const [pr, m] = await Promise.all([
        api.get('/vendor-admin/promotions', cfg).then((r) => r.data),
        vendorAdminApi.menu(vendorId),
      ]);
      setItems(pr);
      setMenu(m || { sections: [] });
    } catch (e) { /* */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [vendorId]);

  const allItems = (menu.sections || []).flatMap((s) => s.items || []);

  const openNew = () => { setEditing(null); setForm({ ...empty }); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...p,
      expires_at: p.expires_at ? p.expires_at.slice(0, 16) : '',
      usage_limit: p.usage_limit ?? null,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.code) { toast.error('Code is required'); return; }
    if (form.scope === 'product' && !form.product_id) {
      toast.error('Choose a product'); return;
    }
    const body = { ...form };
    if (!body.expires_at) delete body.expires_at;
    else body.expires_at = new Date(body.expires_at).toISOString();
    if (!body.usage_limit) body.usage_limit = null;
    if (!body.max_discount_cad) body.max_discount_cad = null;
    body.discount_value = Number(body.discount_value);
    body.min_subtotal_cad = Number(body.min_subtotal_cad || 0);
    try {
      if (editing) {
        const patch = { ...body };
        delete patch.scope; delete patch.product_id; delete patch.created_at;
        await api.patch(`/vendor-admin/promotions/${editing.id}`, patch, cfg);
        toast.success('Updated');
      } else {
        await api.post('/vendor-admin/promotions', body, cfg);
        toast.success('Created');
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.code}?`)) return;
    try { await api.delete(`/vendor-admin/promotions/${p.id}`, cfg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div data-testid="vendor-promotions-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
            <Percent className="h-6 w-6 text-emerald-600" /> Store promotions
          </h1>
          <p className="text-slate-500 mt-1 max-w-2xl">
            Create discount codes for your subtotal or specific products. Codes are entered by customers at checkout.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="vendor-new-promotion-btn">
              <Plus className="h-4 w-4 mr-1.5" />New promo
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? `Edit ${editing.code}` : 'Create promotion'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Code</Label>
                  <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="STORE10" className="h-10 mt-1" />
                </div>
                <div>
                  <Label>Scope</Label>
                  <select value={form.scope} disabled={!!editing}
                    onChange={(e) => setForm({ ...form, scope: e.target.value })}
                    className="h-10 mt-1 w-full rounded-md border border-slate-200 bg-white px-2 text-sm disabled:bg-slate-50">
                    <option value="vendor">Whole-store subtotal</option>
                    <option value="product">Specific item</option>
                  </select>
                </div>
              </div>

              {form.scope === 'product' && (
                <div>
                  <Label>Product</Label>
                  <select value={form.product_id} disabled={!!editing}
                    onChange={(e) => setForm({ ...form, product_id: e.target.value })}
                    className="h-10 mt-1 w-full rounded-md border border-slate-200 bg-white px-2 text-sm disabled:bg-slate-50">
                    <option value="">Select…</option>
                    {allItems.map((it) => <option key={it.id} value={it.id}>{it.name}</option>)}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Discount type</Label>
                  <select value={form.discount_type}
                    onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                    className="h-10 mt-1 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
                    <option value="percent">Percent (%)</option>
                    <option value="flat">Flat (CAD)</option>
                  </select>
                </div>
                <div>
                  <Label>Value</Label>
                  <Input type="number" step="0.5" value={form.discount_value}
                    onChange={(e) => setForm({ ...form, discount_value: e.target.value })} className="h-10 mt-1" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Min subtotal (CAD)</Label>
                  <Input type="number" step="0.5" value={form.min_subtotal_cad || 0}
                    onChange={(e) => setForm({ ...form, min_subtotal_cad: e.target.value })} className="h-10 mt-1" />
                </div>
                <div>
                  <Label>Max discount (CAD, optional)</Label>
                  <Input type="number" step="0.5" value={form.max_discount_cad || ''}
                    onChange={(e) => setForm({ ...form, max_discount_cad: e.target.value || null })} placeholder="No cap" className="h-10 mt-1" />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <Input value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Welcome offer" className="h-10 mt-1" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={!!form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-emerald-600 hover:bg-emerald-700 text-white">{editing ? 'Save' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {items.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-12 bg-white rounded-2xl border border-slate-200 border-dashed">
            No promotions yet.
          </div>
        )}
        {items.map((p) => (
          <div key={p.id} className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={`vendor-promo-card-${p.id}`}>
            <div className="flex items-center justify-between">
              <div className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
                {p.scope === 'product' ? <Box className="h-3.5 w-3.5" /> : <Store className="h-3.5 w-3.5" />}
                {p.scope}
              </div>
              {!p.active && <Badge className="bg-slate-200 text-slate-600 hover:bg-slate-200">Inactive</Badge>}
            </div>
            <div className="mt-2 text-xl font-mono font-semibold text-slate-900">{p.code}</div>
            <div className="text-sm text-slate-500">{p.description || '—'}</div>
            <div className="mt-3 text-sm text-slate-700">
              {p.discount_type === 'percent' ? `${p.discount_value}% off` : `CAD ${Number(p.discount_value).toFixed(2)} off`} subtotal
            </div>
            <div className="mt-2 text-xs text-slate-400">
              Used {p.used_count || 0} • Min CAD {Number(p.min_subtotal_cad || 0).toFixed(2)}
            </div>
            <div className="mt-3 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => openEdit(p)}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
              <Button size="sm" variant="outline" onClick={() => remove(p)} className="text-rose-600 border-rose-200 hover:bg-rose-50">
                <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VendorPromotionsPage;
